import { useEffect, useState } from "react";
import { fr } from "date-fns/locale";
import { CalendarCheck, CalendarClock, CheckCircle2, Loader2, PhoneCall } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { api, apiError } from "../../lib/api";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Toronto" });
const fmtLong = (iso) => new Date(iso).toLocaleString("fr-CA", { dateStyle: "full", timeStyle: "short", timeZone: "America/Toronto" });

const Callback = ({ leadId }) => {
  const [state, setState] = useState("idle");
  const ask = async () => {
    setState("sending");
    try { await api.post("/booking/callback", { lead_id: leadId }); setState("done"); } catch { setState("error"); }
  };
  return (
    <div className="rounded-2xl bg-[#FFFDFB] p-8 text-[#242020]" data-testid="booking-not-connected">
      <CalendarClock className="h-9 w-9 text-[#650D23]" />
      <p className="mt-4 font-display text-xl">L'agenda en ligne sera activé dès la connexion du calendrier Outlook de STRUKTIV.</p>
      <p className="mt-2 text-sm text-[#242020]/70">Aucune disponibilité n'est affichée tant que le calendrier réel n'est pas connecté. En attendant, un conseiller peut vous appeler pour fixer un moment.</p>
      {state === "done" ? (
        <p className="mt-6 flex items-center gap-2 text-[#650D23]" data-testid="booking-callback-success"><CheckCircle2 className="h-5 w-5" />Demande reçue : un conseiller communiquera avec vous.</p>
      ) : (
        <button onClick={ask} disabled={state === "sending"} className="btn-primary mt-6" data-testid="booking-callback-button"><PhoneCall className="h-4 w-4" />Être rappelé par un conseiller</button>
      )}
      {state === "error" && <p className="mt-3 text-sm text-[#b42318]" role="alert">Une erreur est survenue. <a href="/consultation" className="underline">Faire une demande de consultation</a></p>}
    </div>
  );
};

const Confirmed = ({ b, emailSent }) => (
  <div className="rounded-2xl bg-[#FFFDFB] p-8 text-[#242020]" data-testid="booking-confirmed">
    <CalendarCheck className="h-10 w-10 text-[#650D23]" />
    <h3 className="mt-4 text-2xl">Votre consultation est confirmée.</h3>
    <p className="mt-2 text-lg" data-testid="booking-confirmed-when">{fmtLong(b.start)} (heure de Montréal)</p>
    <p className="mt-3 text-sm text-[#242020]/75">La rencontre aura lieu sur Microsoft Teams. {emailSent ? "Une confirmation et une invitation de calendrier ont été envoyées à votre adresse courriel." : "Une invitation de calendrier a été envoyée à votre adresse courriel."}</p>
    {b.join_url && <a href={b.join_url} target="_blank" rel="noopener noreferrer" className="btn-outline mt-5" data-testid="booking-teams-link">Lien de la réunion Teams</a>}
  </div>
);

const Picker = ({ leadId, onBooked }) => {
  const [day, setDay] = useState();
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const max = new Date(); max.setDate(max.getDate() + 30);

  const pick = async (d) => {
    setDay(d); setSlot(null); setSlots(null); setErr("");
    if (!d) return;
    try { setSlots((await api.get("/booking/slots", { params: { date: ymd(d) } })).data.slots); } catch (e) { setErr(apiError(e)); setSlots([]); }
  };
  const confirm = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/booking/book", { lead_id: leadId, start: slot }); onBooked(data); } catch (e) { setErr(apiError(e)); if (day) pick(day); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6 rounded-2xl bg-[#FFFDFB] p-5 text-[#242020] sm:p-8 md:grid-cols-[auto_1fr]" data-testid="booking-picker">
      <Calendar mode="single" locale={fr} selected={day} onSelect={pick} fromDate={today} toDate={max}
        disabled={[{ before: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1) }, { after: max }, { dayOfWeek: [0, 6] }]}
        className="mx-auto rounded-xl border border-[#EDE3D9]" data-testid="booking-calendar" />
      <div>
        <p className="font-display text-xl">{day ? day.toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" }) : "Choisissez une date"}</p>
        {day && slots === null && <Loader2 className="mt-6 h-6 w-6 animate-spin text-[#650D23]" />}
        {slots && !slots.length && !err && <p className="mt-4 text-sm text-[#242020]/70" data-testid="booking-no-slots">Aucune disponibilité ce jour-là. Essayez une autre date.</p>}
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="booking-slots">
          {(slots || []).map((s) => (
            <button key={s} onClick={() => setSlot(s)} data-testid={`booking-slot-${fmtTime(s).replace(/\s|:/g, "")}`}
              className={`rounded-full border px-3 py-2 text-sm transition-colors ${slot === s ? "border-[#650D23] bg-[#650D23] text-[#FFFDFB]" : "border-[#EDE3D9] hover:border-[#650D23]"}`}>{fmtTime(s)}</button>
          ))}
        </div>
        {err && <p className="mt-4 text-sm text-[#b42318]" role="alert" data-testid="booking-error">{err}</p>}
        {slot && <button onClick={confirm} disabled={busy} className="btn-primary mt-6 w-full sm:w-auto" data-testid="booking-confirm-button">{busy ? "Confirmation…" : `Confirmer mon rendez-vous à ${fmtTime(slot)}`}</button>}
      </div>
    </div>
  );
};

export const BookingWidget = ({ leadId, existing }) => {
  const [status, setStatus] = useState(null);
  const [booked, setBooked] = useState(existing || null);
  useEffect(() => {
    api.get("/booking/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
  }, []);
  if (booked) return <Confirmed b={booked.booking} emailSent={booked.email_sent} />;
  if (!status) return <div className="h-80 animate-pulse rounded-2xl bg-[#FFFDFB]/20" />;
  if (!status.configured) return <Callback leadId={leadId} />;
  return <Picker leadId={leadId} onBooked={setBooked} />;
};
